import React, { useState, useMemo } from 'react';
import { Card, Button, Space, Tabs, message, Descriptions, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { ChangePasswordForm, TwoFactorSetupDialog, UserPermissionMatrix, createAuthApi } from '@zero/auth-admin-ui';
import { usePermissions } from '@zero/auth-client';
import { useGridPageSize } from '../../hooks/useGridPageSize.js';

export function ProfilePage({ axios }) {
  const api = useMemo(() => createAuthApi(axios), [axios]);
  const { t } = useTranslation();
  const { context } = usePermissions();
  const [twoFaOpen, setTwoFaOpen] = useState(false);
  const [permPageSize, setPermPageSize] = useGridPageSize('myPermissions', 25);

  const handleChange = (oldPw, newPw) => api.changeMyPassword(oldPw, newPw);
  const handleSetup  = () => api.setup2Fa().then((r) => r.data?.Data ?? r.data);
  const handleVerify = (code) => api.verify2Fa(code);
  const handleDisable= async () => {
    await api.disable2Fa();
    message.success('2FA disabled');
  };

  if (!context) return null;

  return (
    <Tabs defaultActiveKey="info" items={[
      {
        key: 'info', label: t('app:menu.profile'),
        children: (
          <Card size="small">
            <Descriptions size="small" bordered column={1}>
              <Descriptions.Item label={t('user.username')}>{context.Username}</Descriptions.Item>
              {context.FullName && (
                <Descriptions.Item label={t('user.fullName')}>{context.FullName}</Descriptions.Item>
              )}
              {context.Email && (
                <Descriptions.Item label={t('user.email')}>{context.Email}</Descriptions.Item>
              )}
              <Descriptions.Item label="User ID">{context.UserID}</Descriptions.Item>
              <Descriptions.Item label={t('app:dashboard.permissions')}>{context.Permissions?.length ?? 0}</Descriptions.Item>
            </Descriptions>
          </Card>
        ),
      },
      {
        key: 'permissions', label: t('app:dashboard.myPermissions'),
        children: (
          <Card size="small">
            <UserPermissionMatrix
              permissions={context.Permissions ?? []}
              pageSize={permPageSize}
              onPageSizeChange={setPermPageSize}
              scrollY="calc(100vh - 250px)"
            />
          </Card>
        ),
      },
      {
        key: 'password', label: t('changePw.title'),
        children: <ChangePasswordForm onChange={handleChange} />,
      },
      {
        key: '2fa', label: '2FA',
        children: (
          <Space>
            <Button type="primary" onClick={() => setTwoFaOpen(true)}>{t('2fa.setup.title')}</Button>
            <Button onClick={handleDisable}>Disable 2FA</Button>
            <TwoFactorSetupDialog
              open={twoFaOpen}
              onClose={() => setTwoFaOpen(false)}
              onSetup={handleSetup}
              onVerify={handleVerify}
            />
          </Space>
        ),
      },
    ]} />
  );
}
